import { useState } from 'react'
import * as React from 'react'
import dayjs from 'dayjs'
import cx from 'classnames'

import { InfoTooltip } from 'common/InfoTooltip/InfoTooltip'
import { FolderClient } from 'api/clients/FolderClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { FolderIcon } from 'common/FolderIcon'
import { NavSortOrder, AncestorProject, AlmanacFile as File, MinimalFile } from 'types'
import { ReactComponent as RightChevronIcon } from 'assets/icons/chevron-right--small__16.svg'
import { ReactComponent as DownChevronIcon } from 'assets/icons/chevron-down--small__16.svg'
import { Spinner } from 'common/Spinner/Spinner'

import { FolderItem } from '../FolderItem/FolderItem'
import Styles from './FolderRow.module.scss'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface FolderRowProps {
  folder: File
  nestedLevel: number
  selectedFiles: MinimalFile[]
  selectedFolder: AncestorProject | null
  setSelectedFolder: (folder: AncestorProject | null) => void
  setTopLevelFolder: (folder: AncestorProject | null) => void
  workspaceId: number
  openSelectedFolder?: (folder: File | null) => void
  disabled?: boolean
  hideTooltips?: boolean
}

export const FolderRow = ({
  folder,
  nestedLevel,
  workspaceId,
  setSelectedFolder,
  selectedFolder,
  selectedFiles,
  setTopLevelFolder,
  openSelectedFolder,
  hideTooltips = false,
}: FolderRowProps) => {
  const { accessor_names = [], updated_at } = folder
  const { isMobile } = useResponsiveDesign()
  const [folderOpen, setFolderOpen] = useState(false)
  const accessorNameCount = accessor_names?.length || 0
  const [hasFetchedContents, setHasFetchedContents] = useState(false)
  const [nestedFiles, setNestedFiles] = useState<File[]>([])
  const isSelected = selectedFolder?.id === folder.id
  const isDisabled =
    !!selectedFiles.filter((f) => f.type === 'folder' && f.id === folder.id).length || !folder.has_edit_access
  const [fetching, setFetching] = useState<boolean>(false)
  const nestedInset = nestedLevel * 28

  const toggleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setFolderOpen(!folderOpen)

    if (hasFetchedContents) return

    setFetching(true)
    FolderClient.getNestedFiles(folder.id, NavSortOrder.NameAsc, workspaceId).then((res) => {
      if (res.type === SUCCESS_RESPONSE_TYPE) {
        setNestedFiles(res.data.files)
        setHasFetchedContents(true)
      }
      setFetching(false)
    })
  }

  const resetFolders = (folder: AncestorProject) => {
    setSelectedFolder(null)
    setTopLevelFolder(folder)
  }

  const selectFolder = () => {
    if (isDisabled) return
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    setSelectedFolder(folder!)
  }

  const onDoubleClick = () => {
    if (isDisabled) return
    resetFolders(folder)
    openSelectedFolder?.(folder)
  }

  return (
    <>
      <div
        className={cx(Styles.folderRow, { [Styles.selected]: isSelected, [Styles.disabled]: isDisabled })}
        onClick={selectFolder}
        onDoubleClick={onDoubleClick}
      >
        <span style={{ paddingLeft: nestedInset }} className={Styles.titleColumn}>
          <button onClick={toggleOpen} className={Styles.chevronIcon}>
            {
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line no-nested-ternary
              fetching ? (
                <Spinner className={Styles.spinner} />
              ) : folderOpen ? (
                <DownChevronIcon />
              ) : (
                <RightChevronIcon />
              )
            }
          </button>
          <span className={cx(Styles.folderIcon, { [Styles.defaultIcon]: !!folder.default_project })}>
            <FolderIcon defaultFolder={!!folder.default_project} header_emoji={folder.header_emoji} />
          </span>{' '}
          <span className={Styles.title}>{folder.title}</span>
        </span>
        {!isMobile && (
          <span className={Styles.column}>{`${dayjs(updated_at).format('MMM D')} at ${dayjs(updated_at).format(
            'h:mm a',
          )}`}</span>
        )}
        {hideTooltips || accessor_names.length <= 1 ? (
          <div className={Styles.column}>{folder.accessor_info}</div>
        ) : (
          <InfoTooltip
            description={
              <>
                {accessor_names.slice(0, 5).map((name, index) => (
                  <div key={`accessor-name-${index}`}>{name}</div>
                ))}
                {accessorNameCount > 5 && <div>+ {accessorNameCount - 5} More</div>}
              </>
            }
            placement="left"
            className={Styles.narrowTooltip}
          >
            <div className={Styles.column}>{folder.accessor_info}</div>
          </InfoTooltip>
        )}
      </div>
      {!!nestedFiles.length && folderOpen && (
        <>
          {nestedFiles.map((file: File) => (
            <FolderItem
              hideTooltips={hideTooltips}
              key={`folder-${file.id}`}
              folderItem={file}
              nestedLevel={nestedLevel + 1}
              setSelectedFolder={setSelectedFolder}
              workspaceId={workspaceId}
              selectedFolder={selectedFolder}
              setTopLevelFolder={setTopLevelFolder}
              selectedFiles={selectedFiles}
              openSelectedFolder={openSelectedFolder}
            />
          ))}
        </>
      )}
    </>
  )
}
